import { For, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ChevronRightIcon } from '../../components/icons-library/chevron-right';

type PaginationProps = {
    /** 1-based. */
    page: number;
    totalPages: number;
    onChange: (page: number) => void;
    /** "1 - 10 of 38 results" line under the controls. */
    summary?: string;
    ariaLabel?: string;
};

/* How many page numbers show at once; the window slides to keep the current page inside. */
const WINDOW_SIZE = 5;

const visiblePages = (page: number, totalPages: number) => {
    const count = Math.min(WINDOW_SIZE, totalPages);
    const start = Math.min(Math.max(1, page - Math.floor(count / 2)), Math.max(1, totalPages - count + 1));

    return Array.from({ length: count }, (_, i) => start + i);
};

/* DS pagination (Figma 214:60245): outlined prev/next icon buttons either side of a row of
   link-styled page numbers, the current one boxed in turquoise-800. */
export const Pagination = (props: PaginationProps) => {
    const { localize } = useContext(AppContext);

    const isFirst = () => props.page <= 1;
    const isLast = () => props.page >= props.totalPages;

    return (
        <Show when={props.totalPages > 1}>
            <nav
                aria-label={props.ariaLabel ?? localize('pagination', 'Pagination')}
                class="mx-auto flex w-full max-w-cvi-pagination flex-col items-center gap-cvi-6 py-cvi-8"
            >
                <div class="flex w-full flex-wrap items-center justify-center gap-x-cvi-11 gap-y-cvi-4">
                    <button
                        type="button"
                        class="icon-button-secondary icon-button-secondary-sm"
                        disabled={isFirst()}
                        onClick={() => props.onChange(props.page - 1)}
                        aria-label={localize('previous-page', 'Previous page')}
                    >
                        <ChevronRightIcon class="rotate-180" fill="currentColor" size="small" ariaHidden />
                    </button>

                    <div class="flex flex-wrap items-center justify-center gap-cvi-4">
                        <For each={visiblePages(props.page, props.totalPages)}>
                            {(number) => {
                                const isCurrent = () => number === props.page;

                                return (
                                    <button
                                        type="button"
                                        aria-current={isCurrent() ? 'page' : undefined}
                                        aria-label={localize('page-number', 'Page') + ` ${number}`}
                                        onClick={() => props.onChange(number)}
                                        class={clsx(
                                            'h-cvi-pagination-item cursor-pointer rounded-cvi-md border bg-transparent px-cvi-5 py-cvi-4 label-md font-cvi-regular transition-colors duration-200',
                                            isCurrent()
                                                ? 'border-cvi-turquoise-800 text-cvi-turquoise-800'
                                                : 'border-transparent text-cvi-fg-link hover:text-cvi-turquoise-800',
                                        )}
                                    >
                                        {number}
                                    </button>
                                );
                            }}
                        </For>
                    </div>

                    <button
                        type="button"
                        class="icon-button-secondary icon-button-secondary-sm"
                        disabled={isLast()}
                        onClick={() => props.onChange(props.page + 1)}
                        aria-label={localize('next-page', 'Next page')}
                    >
                        <ChevronRightIcon fill="currentColor" size="small" ariaHidden />
                    </button>
                </div>

                <Show when={props.summary}>
                    <p class="m-0 label-sm font-cvi-regular text-cvi-fg-muted">{props.summary}</p>
                </Show>
            </nav>
        </Show>
    );
};
